import { useMemo } from "react";
import type { PanelProps } from "../types";
import { getCisterns, getCisternsList } from "../api/endpoints";
import { useQuery } from "../lib/useQuery";
import { nf, periodLabel, exact } from "../lib/format";
import { isUnverified } from "../lib/dataQuality";
import { CISTERN_KIND_LABEL, cisternMaterialLabel, cisternVM } from "../lib/adapters/resources";
import { usePalette } from "../lib/theme";
import { GRID } from "../components/layout";
import { Card, Section } from "../components/Card";
import { StatTile } from "../components/StatTile";
import { Pill } from "../components/Pill";
import { ChartLegend } from "../components/ChartLegend";
import { Columns } from "../components/Columns";
import { BarsH } from "../components/BarsH";
import { TableToggle } from "../components/TableToggle";
import { DataTable } from "../components/DataTable";
import { Masked, MaskedChart, MaskedValue, UnverifiedBanner } from "../components/Masked";
import { Loader } from "../components/states";

const AREA = "cisterns";

/**
 * Устун диаграммада кўрсатиладиган асосий уч материал.
 *
 * Диққат: булар API'даги `material` **қийматлари** ва `byMaterial` объектига
 * калит бўлиб ишлатилади — таржима қилинмайди, акс ҳолда диаграмма нолга
 * тушиб қоларди. Экранга чиқадиган номи `cisternMaterialLabel()` дан олинади.
 */
const MAIN = ["Азотная к-та цистерны", "Аммиак цистерны", "Сода"];

export function CistPanel({ period, months }: PanelProps) {
  const p = usePalette();
  const key = `${period.from}_${period.to}`;
  const masked = isUnverified(AREA);

  const sumQ = useQuery(`cist-sum_${key}`, (s) => getCisterns(period, { period: "monthly" }, s));
  const listQ = useQuery(`cist-list_${key}`, (s) => getCisternsList(period, { limit: 300 }, s));

  const vm = useMemo(
    () =>
      sumQ.data ? cisternVM(sumQ.data, listQ.data?.rows ?? [], listQ.data?.total ?? 0) : null,
    [sumQ.data, listQ.data],
  );

  const top = vm?.materials.slice(0, 6) ?? [];

  return (
    <>
      <UnverifiedBanner
        area={AREA}
        extra="Reyestrdagi «manba fayl» ustuni takrorlanishni ko'rish imkonini beradi."
      />

      <Loader
        q={sumQ}
        height={260}
        notAvailableWhat="/cisterns"
        isEmpty={() => !vm || vm.materials.length === 0}
        emptyTitle="Ushbu davr uchun sisterna ma'lumoti yo'q"
      >
        {() =>
          vm && (
            <>
              <div className={GRID.g6}>
                {top.map((m, i) => (
                  <StatTile
                    key={m.material}
                    label={cisternMaterialLabel(m.material)}
                    value={<MaskedValue area={AREA}>{exact(m.value)}</MaskedValue>}
                    unit={masked ? undefined : m.kind === "tonne" ? "t" : "sisterna"}
                    stripe={i === 0 ? "var(--s1)" : i === 1 ? "var(--s2)" : undefined}
                    foot={
                      <Pill>
                        <Masked area={AREA}>{nf(m.deliveries)} yetkazish</Masked>
                      </Pill>
                    }
                  />
                ))}
              </div>

              <Section
                className="mt-5"
                title="Oylar kesimida"
                note={`${periodLabel(months)} · sisterna soni`}
              >
                {masked ? (
                  <MaskedChart area={AREA} what="Oylar kesimidagi sisterna diagrammasi" />
                ) : (
                  <Card>
                    <ChartLegend
                      items={MAIN.map((name, i) => ({
                        name: cisternMaterialLabel(name),
                        color: ["var(--s1)", "var(--s2)", "var(--s3)"][i],
                      }))}
                    />
                    <Columns
                      labels={vm.months.map((m) => m.label)}
                      fullLabels={vm.months.map((m) => m.full)}
                      height={230}
                      thick={20}
                      ariaLabel="Oylar kesimida temir yo'l sisternalari soni"
                      vFmt={(v) => nf(v) + " sisterna"}
                      series={MAIN.map((name, i) => ({
                        name: cisternMaterialLabel(name),
                        color: [p.s1, p.s2, p.s3][i],
                        values: vm.months.map((m) => m.byMaterial[name] ?? 0),
                      }))}
                    />
                    <TableToggle
                      caption="Oylar kesimida temir yo'l sisternalari"
                      cols={[
                        { t: "Oy" },
                        ...MAIN.map((n) => ({ t: cisternMaterialLabel(n), num: true as const })),
                      ]}
                      rows={vm.months.map((m) => ({
                        key: m.month,
                        cells: [m.full, ...MAIN.map((n) => nf(m.byMaterial[n] ?? 0))],
                      }))}
                    />
                  </Card>
                )}
              </Section>

              <Section title="Materiallar kesimida" note="davr bo'yicha jami">
                {masked ? (
                  <Card>
                    <DataTable
                      caption="Materiallar kesimida sisterna va yetkazishlar"
                      cols={[
                        { t: "Material" },
                        { t: "Qiymat", num: true },
                        { t: "Yetkazishlar", num: true },
                      ]}
                      rows={vm.materials.map((m) => ({
                        key: m.material,
                        cells: [
                          cisternMaterialLabel(m.material),
                          <Masked area={AREA}>{exact(m.value)}</Masked>,
                          <Masked area={AREA}>{nf(m.deliveries)}</Masked>,
                        ],
                      }))}
                    />
                  </Card>
                ) : (
                  /* Цистерна сони (дона) ва автотранспортдаги тонна — иккита
                     турли ўлчов бирлиги. Бир диаграммада юзлаб тонна ёнида
                     бир нечта цистерна устуни кўринмай кетарди, шунинг учун ҳар
                     бири ўз картасида, ўз шкаласида. */
                  <div className={GRID.g2}>
                    {(["cistern", "tonne"] as const).map((kind) => {
                      const list = vm.materials.filter((m) => m.kind === kind);
                      if (list.length === 0) return null;
                      return (
                        <Card key={kind} title={CISTERN_KIND_LABEL[kind]}>
                          <div className="mt-2">
                            <BarsH
                              ariaLabel={`${CISTERN_KIND_LABEL[kind]} — materiallar kesimida`}
                              rows={list.map((m) => ({
                                label: cisternMaterialLabel(m.material),
                                v: m.value,
                                extra: ["Yetkazishlar", `${nf(m.deliveries)} ta`],
                              }))}
                              rowH={26}
                              padR={74}
                              vName={kind === "cistern" ? "Sisterna" : "Tonna"}
                              vFmt={(v) => exact(v) + (kind === "cistern" ? " sis." : " t")}
                            />
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Section>

              <Section
                title="Harakatlar reyestri"
                note={
                  <Loader q={listQ} height={40} notAvailableWhat="/cisterns/list">
                    {(d) => <>{d.total} ta yozuvdan {d.rows.length} tasi ko'rsatilgan</>}
                  </Loader>
                }
              >
                <Loader q={listQ} height={220} notAvailableWhat="/cisterns/list">
                  {() => (
                    <Card>
                      <DataTable
                        caption="Yuk harakatlari reyestri"
                        cols={[
                          { t: "Sana" },
                          { t: "Vaqt" },
                          { t: "Yuk turi" },
                          { t: "Miqdor", num: true },
                          { t: "Manba fayl", wrap: true },
                        ]}
                        rows={vm.tx.map((t) => ({
                          key: t.key,
                          cells: [
                            <span className="font-mono">{t.day}</span>,
                            <span className="font-mono">{t.time ?? "—"}</span>,
                            cisternMaterialLabel(t.material),
                            <Masked area={AREA}>{exact(t.value)}</Masked>,
                            <span className="text-ink-3">{t.source}</span>,
                          ],
                        }))}
                      />
                    </Card>
                  )}
                </Loader>
              </Section>
            </>
          )
        }
      </Loader>
    </>
  );
}
